import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, InputNumber, Select, message, Drawer, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SafetyCertificateOutlined, EyeOutlined,
  CheckCircleOutlined, ArrowUpOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type GSTConfig } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'

const BASE = '/master/gst'
const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const isGstAbove33 = (c: GSTConfig) => {
  if (!c) return false
  const maxRate = Math.max(Number(c.igstRate || 0), Number(c.cgstRate || 0) + Number(c.sgstRate || 0))
  return maxRate > 33
}

const getEffectiveGstRate = (c: GSTConfig) => {
  if (!c) return 0
  return Number(c.igstRate || 0) > 0 ? Number(c.igstRate) : (Number(c.cgstRate || 0) + Number(c.sgstRate || 0))
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const GSTMaster: React.FC = () => {
  const { gstConfigs: configs, deleteGstConfig, saveGstConfig, masterDataLoaded } = useMasterData()
  const { canAdd, canEdit, canDelete } = useModulePermission('Masters')
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)

  const [viewRecord, setViewRecord] = useState<GSTConfig | null>(null)
  const [igstOnly, setIgstOnly]     = useState(false)   // true when extracted % > 33
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const cgstWatch  = (Form.useWatch('cgstRate',  form) as number | undefined) ?? 0
  const sgstWatch  = (Form.useWatch('sgstRate',  form) as number | undefined) ?? 0
  const igstWatch  = (Form.useWatch('igstRate',  form) as number | undefined) ?? 0
  const totalWatch = igstOnly ? igstWatch : (cgstWatch + sgstWatch)
  const above33    = totalWatch > 33

  const activeCount = configs.filter(c => c.status === 'active').length
  const above33Count = configs.filter(isGstAbove33).length

  const filteredConfigs = configs.filter(c => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      (c.slabName || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || c.status === filterStatus
    return matchSearch && matchStatus
  })

  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      setIgstOnly(false)
    } else if (mode === 'edit' && editId) {
      const rec = configs.find(c =>
        c.id === editId ||
        String(c.id).toLowerCase() === String(editId).toLowerCase() ||
        (c as any).slabName === editId
      )
      if (rec) {
        const isAbove = isGstAbove33(rec)
        setIgstOnly(isAbove)
        form.setFieldsValue({
          ...rec,
          cgstRate: rec.cgstRate != null ? Number(rec.cgstRate) : undefined,
          sgstRate: rec.sgstRate != null ? Number(rec.sgstRate) : undefined,
          igstRate: rec.igstRate != null ? Number(rec.igstRate) : undefined,
        })
      } else if (masterDataLoaded && configs.length > 0) {
        goList()
      }
    }
  }, [mode, editId, configs, masterDataLoaded])

  const handleDelete = async (id: string) => {
    try {
      await deleteGstConfig(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const cgst     = igstOnly ? 0 : (values.cgstRate ?? 0)
    const sgst     = igstOnly ? 0 : (values.sgstRate ?? 0)
    const igst     = igstOnly ? (values.igstRate ?? 0) : (cgst + sgst)
    const isEdit = mode === 'edit'
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('gst_configs') } catch { id = genId('GST') }
    }
    const payload = {
      ...values,
      applicableFor: values.slabName || '',
      cgstRate: cgst,
      sgstRate: sgst,
      igstRate: igst,
      id,
    }

    setSubmitting(true)
    try {
      await saveGstConfig(payload, isEdit)
      message.success(isEdit ? 'GST configuration updated' : 'GST configuration added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Slab Name', dataIndex: 'slabName',
      render: (v: string) => <span style={{ fontWeight: 600, color: '#1677ff' }}>{v}</span>,
    },
    {
      title: 'CGST %', dataIndex: 'cgstRate', align: 'right' as const,
      render: (v: number) => <span style={{ ...MONO, fontWeight: 700 }}>{v}%</span>,
    },
    {
      title: 'SGST %', dataIndex: 'sgstRate', align: 'right' as const,
      render: (v: number) => <span style={{ ...MONO, fontWeight: 700 }}>{v}%</span>,
    },
    {
      title: 'IGST %', dataIndex: 'igstRate', align: 'right' as const,
      render: (v: number, record: GSTConfig) => {
        const effectiveIgst = v || (record.cgstRate + record.sgstRate)
        return <span style={{ ...MONO, fontWeight: 700, color: '#2563EB' }}>{effectiveIgst}%</span>
      },
    },
    {
      title: 'Applies As', key: 'appliesAs',
      render: (_: any, record: GSTConfig) => {
        const isAbove = isGstAbove33(record)
        const rate = getEffectiveGstRate(record)
        return isAbove
          ? <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 600 }}>IGST {rate}% (any state)</span>
          : <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>CGST+SGST within TN · IGST outside</span>
      },
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 130,
      render: (_: any, record: GSTConfig) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>

          {canEdit && (
            <Tooltip title="Edit">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
            </Tooltip>
          )}

          {canDelete && (
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit GST Slab' : 'Add GST Slab'}
        subtitle="GST slab with CGST / SGST rates — region split is applied automatically on invoices"
        onBack={goList}
        extra={mode === 'edit' && editId && canDelete ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: 16 }}
          onValuesChange={(changed) => {
            if (changed.slabName !== undefined) {
              const match = String(changed.slabName).match(/(\d+(?:\.\d+)?)/)
              if (match) {
                const pct = Number(match[1])
                if (pct > 33) {
                  setIgstOnly(true)
                  form.setFieldsValue({ igstRate: pct, cgstRate: undefined, sgstRate: undefined })
                } else {
                  setIgstOnly(false)
                  form.setFieldsValue({ cgstRate: pct / 2, sgstRate: pct / 2, igstRate: undefined })
                }
              }
            }
          }}
        >
          <Form.Item name="slabName" label="Slab Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Electricity Supply 18%" />
          </Form.Item>

          {igstOnly ? (
            <Form.Item name="igstRate" label="IGST %" rules={[{ required: true, message: 'IGST % is required' }]}>
              <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" />
            </Form.Item>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="cgstRate" label="CGST %" rules={[{ required: true, message: 'CGST % is required' }]}>
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} addonAfter="%" />
              </Form.Item>
              <Form.Item name="sgstRate" label="SGST %" rules={[{ required: true, message: 'SGST % is required' }]}>
                <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} addonAfter="%" />
              </Form.Item>
            </div>
          )}

          {/* Region split preview */}
          <div style={{ background: '#F8FAFC', border: '1px solid #E4E8F0', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0B1426', marginBottom: 10 }}>
              How this slab is applied on invoices
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Within TN */}
              <div style={{
                background: '#fff', border: `1px solid ${above33 ? '#FECACA' : '#E4E8F0'}`,
                borderRadius: 6, padding: '10px 12px',
              }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: above33 ? '#FEE2E2' : '#D1FAE5',
                    color: above33 ? '#DC2626' : '#059669',
                  }}>
                    {above33 ? 'Any State' : 'Within Tamil Nadu'}
                  </span>
                </div>
                {above33 ? (
                  <>
                    <div style={{ ...MONO, fontSize: 13, fontWeight: 700, color: '#DC2626' }}>
                      IGST {totalWatch.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: 11, color: '#DC2626', marginTop: 2 }}>
                      Above 33% — IGST only, no CGST/SGST split
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ ...MONO, fontSize: 13, fontWeight: 700, color: '#0B1426' }}>
                      CGST {cgstWatch}% + SGST {sgstWatch}%
                    </div>
                    <div style={{ fontSize: 11, color: '#8A93A6', marginTop: 2 }}>
                      Intra-state · Total {totalWatch.toFixed(1)}%
                    </div>
                  </>
                )}
              </div>

              {/* Outside TN */}
              <div style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ marginBottom: 6 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: '#DBEAFE', color: '#2563EB',
                  }}>
                    Outside Tamil Nadu
                  </span>
                </div>
                <div style={{ ...MONO, fontSize: 13, fontWeight: 700, color: '#2563EB' }}>
                  IGST {totalWatch.toFixed(1)}%
                </div>
                <div style={{ fontSize: 11, color: '#8A93A6', marginTop: 2 }}>Inter-state · 100% IGST</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="status" label="Status" initialValue="active">
              <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
            </Form.Item>
          </div>
          <Form.Item name="description" label="Description">
            <Input placeholder="Short description" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update' : 'Save'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={7} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyCertificateOutlined style={{ color: '#059669' }} /> GST Master
          </h2>
          <p className="page-subtitle">
            GST slabs — ≤33% within Tamil Nadu splits as CGST+SGST; above 33% or outside TN applies as IGST directly
          </p>
        </div>
        {canAdd && <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add GST Config</Button>}
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Configs', value: configs.length, icon: <SafetyCertificateOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Above 33%', value: above33Count, icon: <ArrowUpOutlined style={{ fontSize: 24, color: '#DC2626' }} />, iconBg: 'rgba(220,38,38,0.08)', borderColor: '#DC2626' },
        ]}
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by slab name or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredConfigs}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{viewRecord?.slabName}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{viewRecord?.id}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={400}
        extra={
          <Space>
            {canEdit && (
              <Tooltip title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this record?"
                  description="This action cannot be undone."
                  onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                  okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        }
      >
        {viewRecord && (() => {
          const drAbove33 = isGstAbove33(viewRecord)
          const rate = getEffectiveGstRate(viewRecord)
          return (
            <div>
              <div style={{ marginBottom: 16 }}>
                <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                  {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
                </span>
                {drAbove33 && (
                  <span style={{ marginLeft: 8, fontSize: 11, background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: 99, fontWeight: 700 }}>
                    IGST Only
                  </span>
                )}
              </div>
              <DetailRow label="Slab Name" value={<strong>{viewRecord.slabName}</strong>} />
              <DetailRow label="CGST %" value={<span style={MONO}>{viewRecord.cgstRate}%</span>} />
              <DetailRow label="SGST %" value={<span style={MONO}>{viewRecord.sgstRate}%</span>} />
              <DetailRow label="Total / IGST %" value={<span style={{ ...MONO, color: '#2563EB', fontWeight: 700 }}>{rate}%</span>} />
              <DetailRow
                label="Applies as"
                value={
                  drAbove33
                    ? <span style={{ color: '#DC2626', fontWeight: 600 }}>IGST {rate}% — any state</span>
                    : <span style={{ color: '#059669', fontWeight: 600 }}>CGST+SGST within TN · IGST outside</span>
                }
              />
              <DetailRow label="Description" value={viewRecord.description} />
            </div>
          )
        })()}
      </Drawer>
    </div>
  )
}

export default GSTMaster
